import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Global image lightbox.
 * Props:
 *   images   – string[]   list of image URLs
 *   index    – number     currently visible index
 *   onClose  – () => void
 *   onNav    – (idx) => void
 */
export default function ImageLightbox({ images = [], index = 0, onClose, onNav }) {
  const total = images.length;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && hasPrev) onNav(index - 1);
    if (e.key === 'ArrowRight' && hasNext) onNav(index + 1);
  }, [onClose, onNav, index, hasPrev, hasNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  if (!images[index]) return null;

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close */}
      <button
        className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X size={28} />
      </button>

      {/* Counter */}
      {total > 1 && (
        <span className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm tracking-widest font-work">
          {index + 1} / {total}
        </span>
      )}

      {/* Prev */}
      {hasPrev && (
        <button
          className="absolute left-4 text-white/60 hover:text-white transition-colors p-2"
          onClick={(e) => { e.stopPropagation(); onNav(index - 1); }}
          data-testid="lightbox-prev"
          aria-label="Previous image"
        >
          <ChevronLeft size={40} />
        </button>
      )}

      {/* Image */}
      <img
        src={images[index]}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain select-none shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        draggable={false}
      />

      {/* Next */}
      {hasNext && (
        <button
          className="absolute right-4 text-white/60 hover:text-white transition-colors p-2"
          onClick={(e) => { e.stopPropagation(); onNav(index + 1); }}
          data-testid="lightbox-next"
          aria-label="Next image"
        >
          <ChevronRight size={40} />
        </button>
      )}
    </div>
  );
}
